import React, {FC, PropsWithChildren, useCallback} from 'react';
import styled from 'styled-components';

import {Layer} from '../layers/layer';
import {Reposition, RepositionProps} from '../reposition/reposition';
import {PopoverProps} from './popoverTypes';

/*
 * Props.
 */

export interface RepositionPopoverProps extends PopoverProps, Omit<RepositionProps, 'renderChildren'> {
  renderChildren?: RepositionProps['renderChildren'];
}

/*
 * Style.
 */

interface BackgroundStyleProps {
  $shouldBeVisible?: boolean;
}
const StyledBackgroundDiv = styled.div<BackgroundStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) => (p.$shouldBeVisible ? p.theme.modalBackdrop : p.theme.alphas.transparent)};
`;

/*
 * Component.
 */

export const RepositionPopover: FC<PropsWithChildren<RepositionPopoverProps>> = ({
  className,
  renderChildren,
  children,
  ...props
}) => {
  const renderChildrenDefault = useCallback(() => children, [children]);
  const renderChildrenWithDefault = renderChildren ?? renderChildrenDefault;

  const content = (
    <>
      {props.isExclusive && (
        <StyledBackgroundDiv
          data-testid="popover-backdrop"
          $shouldBeVisible={props.hasVisibleBackground}
          onMouseDown={(event) => {
            // Prevents the active/focused element from blurring when clicking the backdrop.
            event.preventDefault();
          }}
          onClick={props.onRequestClose}
        />
      )}
      <Reposition className={className} renderChildren={renderChildrenWithDefault} {...props} />
    </>
  );

  if (props.isInline) {
    return content;
  }

  return <Layer {...props}>{content}</Layer>;
};
